import { AlertBanner, Button, Modal, Select } from '@insio/ui-toolkit';
import { MasterAccountMemberClassification } from '@insio/shared-types';
import { useDelayedLoading } from '@/hooks/use-delayed-loading';
import { useEnumTranslation } from '@/hooks/useEnumTranslation';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import { useDeclareMasterAccount } from './use-declare-master-account';

const CLASSIFICATION_ORDER: readonly MasterAccountMemberClassification[] = [
  MasterAccountMemberClassification.EMPLOYEE,
  MasterAccountMemberClassification.MASTER_ACCOUNT_MEMBER
];

function isClassification(value: unknown): value is MasterAccountMemberClassification {
  return CLASSIFICATION_ORDER.some(classification => classification === value);
}

interface DeclareMasterAccountModalProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Explains what declaring the organisation a master account does and lets the caller sort every other
 * direct member into the master account or the company before confirming.
 */
export function DeclareMasterAccountModal({ open, onClose }: DeclareMasterAccountModalProps) {
  const t = useFeatureTranslation('systemSettings.organization.masterAccountSection.declareDialog');
  const tClassification = useEnumTranslation('masterAccountMemberClassification');
  const {
    people,
    peopleLoading,
    peopleError,
    ready,
    classificationOf,
    classify,
    declare,
    declaring
  } = useDeclareMasterAccount(open);
  const showPeopleLoading = useDelayedLoading(peopleLoading);

  const classificationOptions = CLASSIFICATION_ORDER.map(classification => ({
    value: classification,
    label: tClassification(classification)
  }));
  const anyMasterAccountMember = people.some(
    person =>
      classificationOf(person.id) === MasterAccountMemberClassification.MASTER_ACCOUNT_MEMBER
  );

  const handleClose = () => {
    if (!declaring) onClose();
  };

  return (
    <Modal open={open} onClose={handleClose} size='sm' testId='declare-master-account-modal'>
      <Modal.Header onClose={handleClose} label={t('title')} />

      <Modal.Content className='h-auto! min-h-0'>
        <div className='space-y-lg px-xs'>
          <div className='space-y-xs'>
            <p className='text-sm text-foreground-base'>{t('intro')}</p>
            <ul className='list-disc space-y-2xs pl-lg text-sm text-foreground-base'>
              <li>{t('ownBooks')}</li>
              <li>{t('masterAccountBoard')}</li>
              <li>{t('subscription')}</li>
            </ul>
          </div>

          {showPeopleLoading && (
            <p className='text-sm text-foreground-muted'>{t('peopleLoading')}</p>
          )}
          {peopleError && <AlertBanner variant='error' title={t('peopleError')} />}

          {ready && people.length > 0 && (
            <section className='space-y-sm' aria-labelledby='declare-master-account-people-title'>
              <div className='space-y-2xs'>
                <h3
                  id='declare-master-account-people-title'
                  className='text-sm font-semibold text-foreground-base'
                >
                  {t('peopleTitle')}
                </h3>
                <p className='text-sm text-foreground-muted'>{t('peopleHint')}</p>
              </div>

              <ul className='divide-y divide-border-base rounded-lg border border-border-base'>
                {people.map(person => (
                  <li
                    key={person.id}
                    className='flex flex-col gap-xs px-md py-sm sm:flex-row sm:items-center sm:justify-between'
                  >
                    <div className='min-w-0 flex-1'>
                      <p className='truncate text-sm font-medium text-foreground-base'>
                        {person.displayName}
                      </p>
                      {person.email && (
                        <p className='truncate text-xs text-foreground-muted'>{person.email}</p>
                      )}
                    </div>
                    <div className='w-full shrink-0 sm:w-48'>
                      <Select
                        id={`declare-master-account-classification-${person.id}`}
                        height='sm'
                        ariaLabel={t('classificationLabel', { name: person.displayName })}
                        value={classificationOf(person.id)}
                        options={classificationOptions}
                        isSearchable={false}
                        disabled={declaring}
                        onChange={value => {
                          if (isClassification(value)) classify(person.id, value);
                        }}
                      />
                    </div>
                  </li>
                ))}
              </ul>

              {anyMasterAccountMember && (
                <AlertBanner
                  variant='warning'
                  title={t('masterAccountMemberWarningTitle')}
                  body={t('masterAccountMemberWarningBody')}
                />
              )}
            </section>
          )}
        </div>
      </Modal.Content>

      <Modal.Footer>
        <Modal.Action>
          <Button variant='default' disabled={declaring} onClick={handleClose}>
            {t('cancel')}
          </Button>
          <Button variant='primary' disabled={!ready || declaring} onClick={() => void declare()}>
            {t('confirm')}
          </Button>
        </Modal.Action>
      </Modal.Footer>
    </Modal>
  );
}
